import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ReactNode} from 'react'
import {TOOL_NAMES} from '../../../constants/tool-names'
import type {Delta, RunCustomSetupStepToolArgs, RunSetupArgs, ToolArgs} from '../../../types/session'
import type {ToolLogMetadataBag} from '../../../types/tool-log-metadata'
import type {ToolHeaderPropsType} from './NewToolHeader'
import {createToolHeaderProps} from './create-tool-header-props'
import {buildToolDurationSuffix} from './ToolDurationSuffix'
import {Tool, ToolPrimaryContent, parseContent} from './ToolPrimitives'

type SetupToolName = typeof TOOL_NAMES.RUN_CUSTOM_SETUP_STEP | typeof TOOL_NAMES.RUN_SETUP

export interface SetupToolProps {
  name: SetupToolName
  args: ToolArgs
  delta: Delta
  metadata?: ToolLogMetadataBag
  shouldShowCodeInsights: boolean
  isInGroup?: boolean
  leadingVisual?: ReactNode
}

export function SetupTool({
  name,
  args,
  delta,
  metadata,
  shouldShowCodeInsights,
  isInGroup = false,
  leadingVisual,
}: SetupToolProps) {
  const {isError, content: output} = parseContent(delta.content)
  const isPending = output === undefined
  const title =
    name === TOOL_NAMES.RUN_CUSTOM_SETUP_STEP
      ? buildCustomSetupStepTitle(args as RunCustomSetupStepToolArgs, isInGroup)
      : (args as RunSetupArgs).name.trim()
  const toolHeaderProps = createSetupToolHeaderProps(
    isInGroup ? undefined : leadingVisual,
    metadata,
    shouldShowCodeInsights,
    isFeatureEnabled('copilot_mission_control_setup_steps_timing'),
    isPending,
  )

  return (
    <Tool title={title} isError={isError} isPending={isPending} isInGroup={isInGroup} toolHeaderProps={toolHeaderProps}>
      <ToolPrimaryContent language="console" markdown={output} shellLoading={isPending} />
    </Tool>
  )
}

export function createSetupToolHeaderProps(
  leadingVisual: ReactNode,
  metadata: ToolLogMetadataBag | undefined,
  shouldShowCodeInsights: boolean,
  shouldShowDuration: boolean,
  isPending: boolean,
): ToolHeaderPropsType {
  const toolHeaderProps = createToolHeaderProps({leadingVisual}, metadata, shouldShowCodeInsights)

  if (shouldShowDuration) {
    const titleSuffix = buildToolDurationSuffix(metadata, isPending)
    if (titleSuffix) {
      toolHeaderProps.titleSuffix = titleSuffix
    }
  }

  return toolHeaderProps
}

function buildCustomSetupStepTitle(args: RunCustomSetupStepToolArgs, isInGroup: boolean) {
  const name = args.name?.trim()
  if (isInGroup) return name || 'Custom setup step'
  return name ? `Run "${name}" custom setup step` : 'Run custom setup step'
}
